import { useEffect, useRef, useState } from 'react';
import { Avatar, IconChevronDown, Skeleton, StatusPill } from '../components';
import { useSession } from '../lib/session';
import styles from './shell.module.css';

/**
 * The agent switcher. Most tenants have one agent in beta, but the model is
 * many-agents-per-tenant from day one, and the console should not have to be
 * rebuilt the first time a client adds a second.
 */
export function AgentSwitcher() {
  const { agents, activeAgent, setActiveAgentId, agentsLoading, agentsError } = useSession();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    menuRef.current?.querySelector<HTMLButtonElement>('button')?.focus();

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (agentsLoading) {
    return <Skeleton variant="block" height="var(--sp-16)" />;
  }

  if (agentsError || agents.length === 0) {
    return (
      <div className={styles.switcherEmpty}>
        {agentsError
          ? 'Could not load agents.'
          : 'No agent has been provisioned for this organisation yet.'}
      </div>
    );
  }

  return (
    <div className={styles.switcher} ref={containerRef}>
      <button
        type="button"
        className={styles.switcherButton}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <Avatar
          name={activeAgent?.name ?? 'Agent'}
          initial={activeAgent?.initial}
          kind="agent"
          size="md"
        />
        <span className={styles.switcherText}>
          <span className={styles.switcherName}>{activeAgent?.name ?? 'Select an agent'}</span>
          <span className={styles.switcherRole}>{activeAgent?.roleLabel ?? 'Agent'}</span>
        </span>
        <IconChevronDown className={styles.switcherChevron} size={18} />
      </button>

      {open ? (
        <ul className={styles.switcherMenu} role="listbox" aria-label="Agents" ref={menuRef}>
          {agents.map((agent) => {
            const selected = agent.id === activeAgent?.id;
            return (
              <li key={agent.id} role="option" aria-selected={selected}>
                <button
                  type="button"
                  className={[
                    styles.switcherOption,
                    selected ? styles.switcherOptionSelected : null,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    setActiveAgentId(agent.id);
                    setOpen(false);
                  }}
                >
                  <Avatar name={agent.name} initial={agent.initial} kind="agent" size="sm" />
                  <span className={styles.switcherText}>
                    <span className={styles.switcherName}>{agent.name}</span>
                    <span className={styles.switcherRole}>{agent.roleLabel}</span>
                  </span>
                  <StatusPill status={agent.status} micro />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
